import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';

// Import components
import Layout from './components/Layout/Layout';
import Login from './pages/Login';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Verify2FA from './pages/Verify2FA';
import Dashboard from './pages/Dashboard';
import PromptTester from './pages/PromptTester';
import PromptHistory from './pages/PromptHistory';
import SecurityRules from './pages/SecurityRules';
import AdminPanel from './pages/AdminPanel';
import Profile from './pages/Profile';
import ApiKeys from './pages/ApiKeys';
import ApiDocs from './pages/ApiDocs';
import NotFound from './pages/NotFound';
import OrganizationManagement from './pages/OrganizationManagement';
import TeamManagement from './pages/TeamManagement';
import UserManagement from './pages/UserManagement';
// import ApprovalWorkflows from './pages/ApprovalWorkflows';
import ModelScanner from './pages/ModelScanner';
import ComplianceDashboard from './pages/ComplianceDashboard';
import AuditLogViewer from './components/AuditLogViewer';
import ComplianceTester from './components/ComplianceTester';
import DemoChatbot from './pages/DemoChatbot';
import IncidentDashboard from './pages/IncidentDashboard';
import IncidentList from './pages/IncidentList';
import IncidentDetails from './pages/IncidentDetails';
import PIIProtectionDemos from './pages/PIIProtectionDemos';
import AIInventory from './pages/AIInventory';
import SuperAdminDashboard from './pages/SuperAdminDashboard';

// Import contexts
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { ThemeContextProvider, useThemeMode } from './contexts/ThemeContext';

// Import types
import './types/api';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  }
});

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <div className="loading-spinner" />
      </Box>
    );
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
};

// Super Admin Route component
const SuperAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <div className="loading-spinner" />
      </Box>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'super_admin') return <Navigate to="/dashboard" replace />;

  return <>{children}</>;
};

// Admin Route component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <div className="loading-spinner" />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Main App Routes component
const AppRoutes: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-2fa" element={<Verify2FA />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="prompt-tester" element={<PromptTester />} />
        <Route path="prompt-history" element={<PromptHistory />} />
        <Route path="security-rules" element={<SecurityRules />} />
        <Route path="model-scanner" element={<ModelScanner />} />
        <Route path="compliance-dashboard" element={<ComplianceDashboard />} />
        <Route path="compliance-testing" element={<ComplianceTester />} />
        <Route path="demo-chatbot" element={<DemoChatbot />} />
        <Route path="pii-demos" element={<PIIProtectionDemos />} />
        <Route path="api-keys" element={<ApiKeys />} />
        <Route path="api-docs" element={<ApiDocs />} />
        <Route path="profile" element={<Profile />} />

        {/* AI Inventory routes */}
        <Route path="ai-inventory" element={<AIInventory />} />

        {/* Incident Management routes */}
        <Route path="incidents/dashboard" element={<IncidentDashboard />} />
        <Route path="incidents/:id" element={<IncidentDetails />} />
        <Route path="incidents" element={<IncidentList />} />

        {/* Admin only routes */}
        <Route
          path="audit-logs"
          element={
            <AdminRoute>
              <AuditLogViewer />
            </AdminRoute>
          }
        />
        <Route
          path="organization"
          element={
            <AdminRoute>
              <OrganizationManagement />
            </AdminRoute>
          }
        />
        <Route
          path="teams"
          element={
            <AdminRoute>
              <TeamManagement />
            </AdminRoute>
          }
        />
        <Route
          path="users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />
        {/* <Route 
          path="approvals" 
          element={
            <AdminRoute>
              <ApprovalWorkflows />
            </AdminRoute>
          } 
        /> */}
        <Route
          path="admin"
          element={
            <AdminRoute>
              <AdminPanel />
            </AdminRoute>
          }
        />
        <Route
          path="super-admin"
          element={
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          }
        />
      </Route>

      {/* 404 route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

// Inner App with theme applied
const ThemedApp: React.FC = () => {
  const { theme, mode } = useThemeMode();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: mode === 'dark' ? '#334155' : '#363636',
                color: '#fff'
              },
              success: {
                style: {
                  background: '#4caf50'
                }
              },
              error: {
                style: {
                  background: '#f44336'
                }
              }
            }}
          />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

// Main App component
const App: React.FC = () => {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeContextProvider>
          <ThemedApp />
        </ThemeContextProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
